import { useEffect, useState } from 'react'

/**
 * Hook that ensures a loading state is shown for a minimum duration to prevent
 * layout shifts and content flashes when content loads very quickly.
 *
 * @param isActuallyLoading - The loading state of the real associated data source
 * @param minimumMs - Minimum time in milliseconds to show loading (default: 300ms)
 * @returns Boolean indicating whether to show loading state
 */
const useMinimumLoadingTime = (isActuallyLoading: boolean, minimumMs: number = 300): boolean => {
  const [showLoading, setShowLoading] = useState(isActuallyLoading)
  const [startTime, setStartTime] = useState<number | null>(null)

  useEffect(() => {
    if (isActuallyLoading && startTime === null) {
      // Start loading - record the start time
      setStartTime(Date.now())
      setShowLoading(true)
    } else if (!isActuallyLoading && startTime !== null) {
      // Content has loaded - check if minimum time has elapsed
      const elapsed = Date.now() - startTime
      const remaining = minimumMs - elapsed

      if (remaining > 0) {
        // Wait for the remaining time before hiding loading
        const timeout = setTimeout(() => {
          setShowLoading(false)
          setStartTime(null)
        }, remaining)

        return () => clearTimeout(timeout)
      }
      // Minimum time already elapsed, hide immediately
      setShowLoading(false)
      setStartTime(null)
    }

    return undefined
  }, [isActuallyLoading, startTime, minimumMs])

  // Reset when we start loading again
  useEffect(() => {
    if (isActuallyLoading) {
      setShowLoading(true)
    }
  }, [isActuallyLoading])

  return showLoading
}

export default useMinimumLoadingTime
